import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateWithYear } from './formatters';
import type { LifecycleMilestone } from './types';
import css from './DigitalTwinView.module.css';

interface LifecycleTimelineProps {
  milestones: {
    birth: LifecycleMilestone;
    weaned: LifecycleMilestone;
    tenWeeks: LifecycleMilestone;
    slaughterReady: LifecycleMilestone;
  };
}

const phaseLabels: Record<string, string> = {
  birth: 'digitalTwin.milestones.birth',
  weaned: 'digitalTwin.milestones.weaned',
  tenWeeks: 'digitalTwin.milestones.tenWeeks',
  slaughterReady: 'digitalTwin.milestones.slaughterReady'
};

function computeRailProgressPercent(items: LifecycleMilestone[]): number {
  let lastPast = -1;
  for (let i = 0; i < items.length; i++) {
    if (items[i].isPast) {
      lastPast = i;
    }
  }
  if (lastPast < 0) {
    return 0;
  }
  const centerTimelinePct = ((lastPast + 0.5) / items.length) * 100;
  const railStartPct = (0.5 / items.length) * 100;
  const railSpanPct = ((items.length - 1) / items.length) * 100;
  const alongRail = (centerTimelinePct - railStartPct) / railSpanPct;
  return Math.min(100, Math.max(0, alongRail * 100));
}

/** Stage the model is currently focused on (next upcoming, or last if all past). */
function computeFocusIndex(items: LifecycleMilestone[]): number {
  const next = items.findIndex((m) => !m.isPast);
  return next >= 0 ? next : items.length - 1;
}

export const LifecycleTimeline: React.FC<LifecycleTimelineProps> = ({ milestones }) => {
  const { t } = useTranslation();

  const items = React.useMemo(
    () => [milestones.birth, milestones.weaned, milestones.tenWeeks, milestones.slaughterReady],
    [milestones]
  );

  const progressPercent = React.useMemo(() => computeRailProgressPercent(items), [items]);
  const focusIndex = React.useMemo(() => computeFocusIndex(items), [items]);

  return (
    <section className={css.lifecycleShell} aria-label={t('digitalTwin.timelineAriaLabel')}>
      <div className={css.lifecycleTrack}>
        <div className={css.lifecycleRailLayer} aria-hidden="true">
          <div className={css.lifecycleRailTrack} />
          <div className={css.lifecycleRailProgress} style={{ width: `${progressPercent}%` }} />
        </div>

        <ol className={css.lifecycleSteps}>
          {items.map((m, index) => {
            const isFocus = index === focusIndex;
            return (
              <li
                key={m.phase}
                className={`${css.lifecycleMilestone} ${isFocus ? css.lifecycleMilestoneHighlight : ''}`}
              >
                <div className={css.lifecycleDotWrap}>
                  <span
                    className={`${css.lifecycleDot} ${
                      m.isPast ? css.lifecycleDotPast : css.lifecycleDotFuture
                    } ${isFocus && !m.isPast ? css.lifecycleDotCurrent : ''}`}
                  />
                </div>
                <div className={css.lifecycleTextBlock}>
                  <span className={css.lifecycleLabelRow}>
                    {m.isEstimated && (
                      <span className={css.lifecycleEstimated} aria-hidden="true">
                        ~
                      </span>
                    )}
                    <span className={`${css.lifecycleLabel} ${m.isEstimated ? css.lifecycleLabelEstimated : ''}`}>
                      {t(phaseLabels[m.phase] ?? 'digitalTwin.milestones.birth')}
                    </span>
                  </span>
                  <time className={css.lifecycleDate} dateTime={m.date}>
                    {formatDateWithYear(m.date)}
                  </time>
                  <span className={css.lifecycleWeight}>
                    {m.weightKg.toFixed(1)} {t('digitalTwin.units.kg')}
                  </span>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
};
